import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import {
  AxiosHookConfig,
  isServerError,
  ServerError,
} from 'src/hooks/use-axios';
import { useNotification } from 'src/hooks/use-notifications';
import {
  shiftBerlinZoneToTso,
  shiftTsoZoneToBerlin,
} from 'src/pages/direct-fcfs/support/date';
import {
  DirectFcFsError,
  PaginatedData,
  ValidityPeriod,
} from 'src/types/direct-fcfs';

export function paginatedResponseDataConverter<T, R = T>(
  converter: (data: T) => R
) {
  return (response: AxiosResponse<PaginatedData<T>>) => ({
    ...response,
    data: {
      ...response.data,
      values: response.data.values.map((value) => converter(value)),
    },
  });
}

export function responseDataConverter<T, R = T>(converter: (data: T) => R) {
  return (response: AxiosResponse<T>) => ({
    ...response,
    data: converter(response.data),
  });
}

export function tsoZonedDateTimeSupport(date: string, tsoZone: string): string {
  return shiftTsoZoneToBerlin(date, tsoZone);
}

export function validityPeriodTsoZoneSupport(
  validity: ValidityPeriod,
  tsoZone: string
): ValidityPeriod {
  return {
    from: shiftTsoZoneToBerlin(validity.from, tsoZone),
    to: shiftTsoZoneToBerlin(validity.to, tsoZone),
  };
}

export function shiftValidityPeriodBerlinZoneToTso(
  validity: ValidityPeriod,
  tsoZone: string
): ValidityPeriod {
  return {
    from: shiftBerlinZoneToTso(validity.from, tsoZone),
    to: shiftBerlinZoneToTso(validity.to, tsoZone),
  };
}

export function isDirectFcfsError(
  err: unknown
): err is ServerError<DirectFcFsError> {
  if (!isServerError(err)) return false;
  if (err.response.status === 401 || err.response.status === 403) return false;
  const data = err.response?.data;
  if (!data) return false;
  const error = data as DirectFcFsError;
  return !(!error.error || !error.message || !error.status || !error.timestamp);
}

export function useExtendAxiosConfig(current: AxiosHookConfig) {
  const notify = useNotification();

  const axiosHookConfig = useMemo<AxiosHookConfig>(
    () => ({
      ...current,
      onError(error, meta) {
        if (!meta.isPageLoadError) {
          if (isDirectFcfsError(error)) {
            const data = error.response.data;
            notify({
              type: 'error',
              children: `${data.message}`,
            });
            return;
          }
        }
        current.onError?.(error, meta);
      },
    }),
    [current, notify]
  );

  return axiosHookConfig;
}
